import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { apiError, useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { hasSpecialPermission } from "@/lib/permissions";
import { toast } from "sonner";
import {
  CaretLeft, Trash, Plus, Eye, LockKey, LockKeyOpen, PencilSimple, Check, X, WarningCircle,
  Printer, ClockCounterClockwise, FileXls, UsersThree, EyeSlash, Bell,
} from "@phosphor-icons/react";

const money = (n) => (Number(n) || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
const cellIn = "w-full h-full px-2 py-1.5 outline-none border-none bg-transparent font-mono text-sm text-right focus:bg-emerald-50 transition-colors disabled:bg-transparent disabled:text-slate-500";
const th = "px-2 py-1.5 text-xs font-semibold text-slate-600 bg-slate-100 border border-slate-300 text-left whitespace-nowrap";
const td = "border border-slate-300 p-0 align-middle";
const SHEET_CATEGORY = "Costing Sheet";

export default function OrderCosting() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [o, setO] = useState(null);
  const [lineItems, setLineItems] = useState([{ name: "", qty: 1, price: 0 }]);
  const [taxPercent, setTaxPercent] = useState(18);
  const [advanceEntries, setAdvanceEntries] = useState([]);
  const [advanceDialogOpen, setAdvanceDialogOpen] = useState(false);
  const [advanceAmount, setAdvanceAmount] = useState("");
  const [advanceDate, setAdvanceDate] = useState("");
  const [advanceNote, setAdvanceNote] = useState("");
  const [savingAdvance, setSavingAdvance] = useState(false);
  const [specialNote, setSpecialNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [editMaster, setEditMaster] = useState(false);
  const [masterForm, setMasterForm] = useState({ gst: "", billing_address: "", notes: "" });
  const [savingMaster, setSavingMaster] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [printingDelivery, setPrintingDelivery] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [manageFor, setManageFor] = useState(null);
  const [teamMembers, setTeamMembers] = useState([]);
  const [selectedViewers, setSelectedViewers] = useState([]);
  const [savingAccess, setSavingAccess] = useState(false);
  const [requestingIds, setRequestingIds] = useState([]);

  const load = () => api.get(`/orders/${id}`).then((r) => {
    setO(r.data);
    const c = r.data.costing;
    if (c) {
      setLineItems(c.line_items && c.line_items.length ? c.line_items : [{ name: "", qty: 1, price: 0 }]);
      setTaxPercent(c.tax_percent ?? 18);
      setAdvanceEntries(c.advance_entries || []);
      setSpecialNote(c.special_note || "");
    }
    const cm = r.data.customer_master;
    if (cm) setMasterForm({ gst: cm.gst || "", billing_address: cm.billing_address || "", notes: cm.notes || "" });
  });
  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!o) return <div className="p-8 text-slate-500 text-sm">Loading…</div>;

  const costing = o.costing || {};
  const locked = !!costing.locked;
  const override = hasSpecialPermission(user, "costing_override");
  const isCreator = o.created_by === user.id;
  const canEditRole = user.role === "admin" || user.role === "manager" || override || isCreator;
  const canEdit = canEditRole && !locked;
  const canUnlock = user.role === "admin" || override;
  const canEditMaster = user.role === "admin" || override;
  const canManageAccess = user.role === "admin" || hasSpecialPermission(user, "file_override");
  const canExport = user.role === "admin" || override || isCreator;
  const projectRefEntry = (o.details || []).find((d) => (d.title || "").toLowerCase().includes("project ref"));

  const sheetFiles = (o.attachments || []).filter((a) => a.category === SHEET_CATEGORY);
  const orderFiles = (o.attachments || []).filter((a) => a.category !== SHEET_CATEGORY);
  const canViewOrderFile = (a) => canManageAccess || a.uploaded_by_id === user.id || (a.allowed_viewers || []).includes(user.id);
  const hasPendingRequest = (a) => (o.pending_access_requests || []).some((r) => r.attachment_id === a.id && r.requester_id === user.id);

  const RECAP = [
    { d: "Company Name", v: o.customer },
    { d: "Contact Person", v: o.contact_person },
    { d: "DVEPL Ref Code", v: o.customer_code },
    { d: "Date of Order", v: o.order_date },
    { d: "Date of Commitment", v: o.target_dispatch },
    { d: "Project Ref", v: projectRefEntry ? projectRefEntry.value : null, testid: "costing-recap-project_ref" },
  ];

  const value = lineItems.reduce((s, li) => s + (Number(li.qty) || 0) * (Number(li.price) || 0), 0);
  const taxAmount = value * ((Number(taxPercent) || 0) / 100);
  const advanceSum = advanceEntries.reduce((s, e) => s + (Number(e.amount) || 0), 0);
  const totalAmount = value + taxAmount - advanceSum;

  const addRow = () => setLineItems((p) => [...p, { name: "", qty: 1, price: 0 }]);
  const removeRow = (i) => setLineItems((p) => p.filter((_, idx) => idx !== i));
  const updRow = (i, k, v) => setLineItems((p) => p.map((li, idx) => (idx === i ? { ...li, [k]: v } : li)));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        line_items: lineItems.filter((li) => (li.name || "").trim()).map((li) => ({ name: li.name, qty: Number(li.qty) || 0, price: Number(li.price) || 0 })),
        tax_percent: Number(taxPercent) || 0, special_note: specialNote || null,
      };
      await api.put(`/orders/${id}/costing`, payload);
      toast.success("Costing saved — sheet is now locked");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const openAdvanceDialog = () => {
    setAdvanceAmount(""); setAdvanceDate(new Date().toISOString().slice(0, 10)); setAdvanceNote("");
    setAdvanceDialogOpen(true);
  };
  const addAdvance = async () => {
    const amt = Number(advanceAmount);
    if (!amt || amt <= 0) { toast.error("Enter an amount greater than 0"); return; }
    setSavingAdvance(true);
    try {
      await api.post(`/orders/${id}/costing/advance`, { amount: amt, date: advanceDate || undefined, note: advanceNote || undefined });
      toast.success("Advance recorded");
      setAdvanceDialogOpen(false);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingAdvance(false); }
  };
  const deleteAdvance = async (entryId) => {
    if (!window.confirm("Remove this advance entry? This cannot be undone.")) return;
    try { await api.delete(`/orders/${id}/costing/advance/${entryId}`); toast.success("Advance entry removed"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };

  const unlock = async () => {
    setUnlocking(true);
    try { await api.post(`/orders/${id}/costing/unlock`); toast.success("Sheet unlocked for editing"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUnlocking(false); }
  };

  const saveMaster = async () => {
    if (!o.customer_id) return;
    setSavingMaster(true);
    try {
      await api.put(`/customers/${o.customer_id}`, masterForm);
      toast.success("Customer Master updated");
      setEditMaster(false);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingMaster(false); }
  };

  const readFile = (file) => new Promise((res) => { const r = new FileReader(); r.onload = () => res({ name: file.name, type: file.type, size: file.size, data: r.result }); r.readAsDataURL(file); });
  const addFiles = async (e) => {
    const picked = Array.from(e.target.files);
    e.target.value = "";
    if (!picked.length) return;
    setUploading(true);
    try {
      for (const f of picked) {
        if (f.size > 50 * 1024 * 1024) { toast.error(`${f.name} exceeds 50MB, skipped`); continue; }
        const att = await readFile(f);
        await api.post(`/orders/${id}/attachments`, { ...att, category: SHEET_CATEGORY });
      }
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };
  const delSheetFile = async (aid) => {
    try { await api.delete(`/orders/${id}/attachments/${aid}`); toast.success("File removed"); load(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const getFileData = async (a, ctx) => {
    if (a.data) return a.data;
    const { data } = await api.get(`/orders/${id}/attachments/${a.id}/data${ctx ? `?context=${ctx}` : ""}`);
    return data.data;
  };
  const viewFile = async (a, ctx) => {
    const w = window.open("about:blank", "_blank");
    try {
      const dataUrl = await getFileData(a, ctx);
      const parts = dataUrl.split(",");
      const mime = (parts[0].match(/data:(.*?);/) || [])[1] || "application/octet-stream";
      const bin = atob(parts[1]);
      const arr = new Uint8Array(bin.length);
      for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
      const url = URL.createObjectURL(new Blob([arr], { type: mime }));
      if (w && !w.closed) w.location.href = url;
      else window.open(url, "_blank");
    } catch (err) {
      if (w && !w.closed) w.close();
      toast.error(apiError(err.response?.data?.detail) || "Could not open this file");
    }
  };

  const requestAccess = async (a) => {
    setRequestingIds((p) => [...p, a.id]);
    try {
      const { data } = await api.post(`/orders/${id}/attachments/${a.id}/request-access`);
      if (data.already_requested) toast.info("You already requested access — admin has been notified.");
      else toast.success("Access request sent to admin.");
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail) || "Could not send request"); }
    finally { setRequestingIds((p) => p.filter((x) => x !== a.id)); }
  };

  const openManageAccess = async (a) => {
    setManageFor(a);
    setSelectedViewers(a.allowed_viewers || []);
    if (!teamMembers.length) {
      try { const r = await api.get("/users?active=true"); setTeamMembers(r.data.filter((u) => u.role !== "admin")); }
      catch { /* ignore */ }
    }
  };
  const toggleViewer = (uid) => setSelectedViewers((p) => (p.includes(uid) ? p.filter((x) => x !== uid) : [...p, uid]));
  const saveAccess = async () => {
    setSavingAccess(true);
    try {
      await api.put(`/orders/${id}/attachments/${manageFor.id}/permissions`, { allowed_viewers: selectedViewers });
      toast.success("File access updated");
      setManageFor(null);
      load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setSavingAccess(false); }
  };

  const blobErrorDetail = async (err) => {
    try {
      const blob = err.response?.data;
      if (blob instanceof Blob) return JSON.parse(await blob.text()).detail;
    } catch { /* ignore */ }
    return err.response?.data?.detail;
  };

  const printQuotation = async () => {
    setPrinting(true);
    const w = window.open("about:blank", "_blank");
    try {
      const res = await api.get(`/orders/${id}/costing/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (w && !w.closed) w.location.href = url; else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (w && !w.closed) w.close();
      toast.error(apiError(await blobErrorDetail(err)) || "Save the costing sheet first, then print");
    } finally { setPrinting(false); }
  };

  const downloadExcel = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/orders/${id}/costing/excel`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Costing-${o.order_no}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { toast.error(apiError(await blobErrorDetail(err)) || "Save the costing sheet first, then export"); }
    finally { setExporting(false); }
  };

  const printDeliveryNote = async () => {
    setPrintingDelivery(true);
    const w = window.open("about:blank", "_blank");
    try {
      const res = await api.get(`/orders/${id}/delivery-note/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (w && !w.closed) w.location.href = url; else window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (w && !w.closed) w.close();
      toast.error(apiError(await blobErrorDetail(err)) || "Could not generate the Delivery Note");
    } finally { setPrintingDelivery(false); }
  };

  const historyLabel = (h) => h.action === "unlocked" ? "Unlocked" : h.action === "saved" ? "Saved" : h.action;

  return (
    <div className="p-4 md:px-8 py-6 w-full mx-auto animate-fade-in" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => navigate(`/orders/${id}`)} data-testid="back-to-order" className="text-base font-bold text-slate-700 hover:text-emerald-700 flex items-center gap-1.5 transition-colors">
          <CaretLeft size={18} weight="bold" /> Back to order
        </button>
      </div>

      <div className="mb-5">
        <div className="text-base uppercase tracking-[0.2em] text-emerald-700 font-extrabold mb-1">{o.order_no}</div>
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Account Section</h1>
        <p className="text-sm text-slate-500 mt-1">Costing & quotation sheet — for the customer, separate from internal purchase orders.</p>
      </div>

      {/* Locked / editable state banner */}
      {costing.line_items ? (
        locked ? (
          <div className="flex items-center justify-between gap-3 bg-amber-50 text-amber-900 border-l-4 border-amber-500 px-4 py-3 mb-5 rounded-sm" data-testid="costing-locked-banner">
            <div className="flex items-start gap-2">
              <LockKey size={18} className="mt-0.5 shrink-0" />
              <div className="text-sm">
                <div className="font-semibold">This sheet is locked — saved on {new Date(costing.locked_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })} by {costing.locked_by_name}.</div>
                {canUnlock ? (
                  <div className="text-xs text-amber-800/80 mt-0.5">As admin / permitted user you can unlock it to make changes.</div>
                ) : (
                  <div className="text-xs text-amber-800/80 mt-0.5">No further edits are allowed. Contact your admin to request changes.</div>
                )}
              </div>
            </div>
            {canUnlock && (
              <Button onClick={unlock} disabled={unlocking} data-testid="unlock-sheet-button" size="sm" className="bg-amber-500 hover:bg-amber-600 text-white shrink-0 gap-1.5">
                <LockKeyOpen size={14} /> {unlocking ? "Unlocking…" : "Unlock to Edit"}
              </Button>
            )}
          </div>
        ) : (
          <div className="flex items-center gap-2 bg-emerald-50 text-emerald-800 border-l-4 border-emerald-500 px-4 py-2.5 mb-5 rounded-sm text-sm" data-testid="costing-editable-banner">
            <LockKeyOpen size={16} /> This sheet is unlocked and editable. Saving will lock it again.
          </div>
        )
      ) : null}

      {/* Recap strip */}
      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-5 border border-slate-300 bg-white rounded-sm p-4 shadow-sm" data-testid="costing-recap">
        {RECAP.map((f) => (
          <div key={f.d}>
            <div className="text-xs font-medium text-slate-600 mb-1">{f.d}</div>
            <div className="text-sm text-slate-900" data-testid={f.testid || `costing-recap-${f.d.toLowerCase().replace(/\s+/g, "-")}`}>{f.v || "—"}</div>
          </div>
        ))}
      </div>

      {/* Customer Master block */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-5 border border-slate-300 bg-white rounded-sm p-4 shadow-sm" data-testid="customer-master-card">
        <div className="md:col-span-3 flex items-center justify-between">
          <h3 className="text-base font-medium text-slate-800">Company Details (Customer Master)</h3>
          {canEditMaster && o.customer_id && !editMaster && (
            <button onClick={() => setEditMaster(true)} data-testid="edit-customer-master-btn" className="text-xs font-semibold text-emerald-700 hover:underline flex items-center gap-1"><PencilSimple size={12} /> Edit</button>
          )}
        </div>
        {!o.customer_id && <div className="md:col-span-3 text-xs text-slate-500">No linked customer record.</div>}
        {o.customer_id && !editMaster && (
          <>
            <div><div className="text-xs font-medium text-slate-600 mb-1">GST Number</div><div className="text-sm text-slate-900 font-mono" data-testid="master-gst">{masterForm.gst || "—"}</div></div>
            <div className="md:col-span-2"><div className="text-xs font-medium text-slate-600 mb-1">Billing Address</div><div className="text-sm text-slate-900" data-testid="master-billing-address">{masterForm.billing_address || "—"}</div></div>
            <div className="md:col-span-3"><div className="text-xs font-medium text-slate-600 mb-1">Special Notes (Customer Master)</div><div className="text-sm text-slate-900 whitespace-pre-wrap" data-testid="master-notes">{masterForm.notes || "—"}</div></div>
          </>
        )}
        {o.customer_id && editMaster && (
          <>
            <div><label className="text-xs font-medium text-slate-600 mb-1 block">GST Number</label><Input value={masterForm.gst} onChange={(e) => setMasterForm((p) => ({ ...p, gst: e.target.value }))} className="border-slate-300" data-testid="master-gst-input" /></div>
            <div className="md:col-span-2"><label className="text-xs font-medium text-slate-600 mb-1 block">Billing Address</label><Input value={masterForm.billing_address} onChange={(e) => setMasterForm((p) => ({ ...p, billing_address: e.target.value }))} className="border-slate-300" data-testid="master-billing-address-input" /></div>
            <div className="md:col-span-3"><label className="text-xs font-medium text-slate-600 mb-1 block">Special Notes</label><Textarea value={masterForm.notes} onChange={(e) => setMasterForm((p) => ({ ...p, notes: e.target.value }))} className="border-slate-300 text-sm" rows={2} data-testid="master-notes-input" /></div>
            <div className="md:col-span-3 flex gap-2">
              <Button size="sm" onClick={saveMaster} disabled={savingMaster} data-testid="save-customer-master-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-1"><Check size={13} /> {savingMaster ? "Saving…" : "Save"}</Button>
              <Button size="sm" variant="outline" onClick={() => setEditMaster(false)} data-testid="cancel-customer-master-btn" className="border-slate-300 gap-1"><X size={13} /> Cancel</Button>
            </div>
          </>
        )}
      </div>

      {/* Files carried over from New Order / other tabs — view-only, admin-permissioned */}
      <div className="mb-5">
        <div className="text-xs font-medium text-slate-600 mb-1.5">Files from New Order / Other Tabs — view only, admin decides who can open each file</div>
        <div className="flex flex-col gap-2 w-full border border-slate-300 bg-white rounded-sm p-3 shadow-sm" data-testid="costing-order-files">
          {orderFiles.length === 0 && <span className="text-xs text-slate-500">No files uploaded elsewhere on this order yet.</span>}
          {orderFiles.map((a) => {
            const permitted = canViewOrderFile(a);
            return (
              <div key={a.id} className={`flex items-center gap-2 flex-wrap text-xs border rounded-sm px-2.5 py-1.5 ${permitted ? "border-slate-200 bg-slate-50" : "border-amber-200 bg-amber-50"}`} data-testid={`order-file-row-${a.id}`}>
                <button type="button" onClick={() => permitted ? viewFile(a, "costing") : toast.error("This file requires admin permission to view. Contact your admin to request access.")}
                  className={`flex items-center gap-1.5 font-semibold ${permitted ? "text-emerald-700 hover:underline" : "text-slate-400 cursor-not-allowed"}`} data-testid={`order-file-view-${a.id}`}>
                  {permitted ? <Eye size={13} /> : <EyeSlash size={13} />} {a.name}
                </button>
                <span className="text-slate-500">· uploaded by {a.uploaded_by} · {a.uploaded_at ? new Date(a.uploaded_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"} · stored in this Order's Documents</span>
                {!permitted && (
                  <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-700 border border-amber-300" data-testid={`order-file-restricted-${a.id}`}>
                    <LockKey size={10} /> Restricted
                  </span>
                )}
                {!permitted && (
                  hasPendingRequest(a) ? (
                    <span className="inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-sm bg-slate-100 text-slate-500 border border-slate-300" data-testid={`access-requested-${a.id}`}>
                      <Bell size={11} /> Requested — awaiting admin
                    </span>
                  ) : (
                    <button type="button" onClick={() => requestAccess(a)} disabled={requestingIds.includes(a.id)} data-testid={`request-access-${a.id}`}
                      className="text-[10px] font-semibold px-2 py-0.5 rounded-sm border border-amber-400 text-amber-700 hover:bg-amber-100 flex items-center gap-1 disabled:opacity-50">
                      <Bell size={11} /> {requestingIds.includes(a.id) ? "Sending…" : "Request Access"}
                    </button>
                  )
                )}
                {(a.allowed_viewers || []).length > 0 && (
                  <span className="text-[10px] text-slate-400">granted to {(a.allowed_viewers || []).length} member{(a.allowed_viewers || []).length > 1 ? "s" : ""}</span>
                )}
                {canManageAccess && (
                  <button type="button" onClick={() => openManageAccess(a)} data-testid={`manage-access-${a.id}`}
                    className={`ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-sm border flex items-center gap-1 ${
                      (a.allowed_viewers || []).length > 0
                        ? "border-emerald-400 text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
                        : "border-red-400 text-red-700 bg-red-50 hover:bg-red-100"
                    }`}>
                    <UsersThree size={11} /> Manage Access {(a.allowed_viewers || []).length > 0 ? "(Unblocked)" : "(Blocked)"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Files added directly on this sheet — fully open to whoever can see/edit it */}
      <div className="mb-5">
        <div className="text-xs font-medium text-slate-600 mb-1.5">Files Added On This Account Section — view, upload or delete freely</div>
        <div className="flex flex-col gap-2 w-full border border-slate-300 bg-white rounded-sm p-3 shadow-sm" data-testid="costing-files">
          {sheetFiles.length === 0 && <span className="text-xs text-slate-500">No files added on this sheet yet.</span>}
          {sheetFiles.map((a) => (
            <div key={a.id} className="flex items-center gap-2 flex-wrap text-xs border border-slate-200 bg-slate-50 rounded-sm px-2.5 py-1.5" data-testid={`costing-file-row-${a.id}`}>
              <button type="button" onClick={() => viewFile(a)} className="flex items-center gap-1.5 font-semibold text-emerald-700 hover:underline" data-testid={`costing-file-view-${a.id}`}>
                <Eye size={13} /> {a.name}
              </button>
              <span className="text-slate-500">· uploaded by {a.uploaded_by} · {a.uploaded_at ? new Date(a.uploaded_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "—"} · stored in this Order's Documents</span>
              {canEdit && (
                <button type="button" onClick={() => delSheetFile(a.id)} className="ml-auto text-slate-400 hover:text-red-500 p-1" data-testid={`costing-file-delete-${a.id}`}><Trash size={13} /></button>
              )}
            </div>
          ))}
          {canEdit && (
            <label className="text-xs font-semibold text-emerald-700 hover:underline cursor-pointer" data-testid="costing-add-files">
              {uploading ? "Uploading…" : "+ Add More Files"}
              <input type="file" multiple className="hidden" onChange={addFiles} disabled={uploading} />
            </label>
          )}
        </div>
        <p className="text-[10px] text-slate-400 mt-1">Max file size: 50 MB per file.</p>
      </div>

      {/* Excel-style spreadsheet */}
      <div className="mb-2">
        <div className="text-xs font-medium text-slate-600 mb-1.5 flex items-center gap-2">
          Details As per Project
          {locked && (
            <span onClick={canUnlock ? unlock : undefined} data-testid="costing-inline-locked-hint"
              className={`inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-300 px-1.5 py-0.5 rounded-sm ${canUnlock ? "cursor-pointer hover:bg-amber-100" : ""}`}>
              <LockKey size={10} /> Locked — nothing below (incl. Advance) can be edited{canUnlock ? ", click to unlock" : ""}
            </span>
          )}
        </div>
        <div className="w-full overflow-x-auto border border-slate-300 mb-1" data-testid="costing-line-items">
          <table className="w-full border-collapse" role="grid" aria-readonly={!canEdit}>
            <thead>
              <tr>
                <th className={`${th} text-center w-10`}>#</th>
                <th className={th}>Panel Name</th>
                <th className={`${th} text-right w-24`}>Qty</th>
                <th className={`${th} text-right w-32`}>Price</th>
                <th className={`${th} text-right w-36`}>Total</th>
                <th className={`${th} w-10`} />
              </tr>
            </thead>
            <tbody>
              {lineItems.map((li, i) => (
                <tr key={i} data-testid={`costing-row-${i}`} className="hover:bg-slate-50">
                  <td className={`${td} text-center text-xs text-slate-400 bg-slate-50 w-10`}>{i + 1}</td>
                  <td className={td}>
                    <input value={li.name} onChange={(e) => updRow(i, "name", e.target.value)} disabled={!canEdit}
                      className="w-full h-full px-2 py-1.5 outline-none border-none bg-transparent text-sm focus:bg-emerald-50 transition-colors disabled:bg-transparent disabled:text-slate-500"
                      data-testid={`costing-row-name-${i}`} placeholder="S.No or Name of Panel" />
                  </td>
                  <td className={td}><input type="number" min="0" value={li.qty} onChange={(e) => updRow(i, "qty", e.target.value)} disabled={!canEdit} className={cellIn} data-testid={`costing-row-qty-${i}`} /></td>
                  <td className={td}><input type="number" min="0" value={li.price} onChange={(e) => updRow(i, "price", e.target.value)} disabled={!canEdit} className={cellIn} data-testid={`costing-row-price-${i}`} /></td>
                  <td className={`${td} bg-slate-50`}><div className="px-2 py-1.5 text-sm font-mono text-right text-slate-900" data-testid={`costing-row-total-${i}`}>{money((Number(li.qty) || 0) * (Number(li.price) || 0))}</div></td>
                  <td className={`${td} text-center`}>
                    {canEdit && lineItems.length > 1 && (
                      <button type="button" onClick={() => removeRow(i)} className="text-slate-400 hover:text-red-500 p-1" data-testid={`costing-row-remove-${i}`}><Trash size={13} /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {canEdit && (
          <button type="button" onClick={addRow} data-testid="costing-add-row" className="text-xs font-semibold text-emerald-700 hover:underline flex items-center gap-1.5 mb-6">
            <Plus size={13} /> Add row
          </button>
        )}
      </div>

      {/* Summary band */}
      <div className="flex justify-end mb-6">
        <table className="border-collapse border border-slate-300 w-full max-w-sm" data-testid="costing-summary">
          <tbody>
            <tr>
              <td className="border border-slate-300 px-3 py-2 text-sm text-slate-600 bg-slate-50">Value</td>
              <td className="border border-slate-300 px-3 py-2 text-sm font-mono text-right font-semibold text-slate-900" data-testid="costing-value">{money(value)}</td>
            </tr>
            <tr>
              <td className="border border-slate-300 px-3 py-2 text-sm text-slate-600 bg-slate-50">Tax %</td>
              <td className="border border-slate-300 p-0">
                <div className="flex items-center justify-end gap-2 px-2">
                  <input type="number" min="0" value={taxPercent} onChange={(e) => setTaxPercent(e.target.value)} disabled={!canEdit} className="w-14 h-8 outline-none border-none bg-transparent font-mono text-sm text-right disabled:text-slate-500" data-testid="costing-tax-percent" />
                  <span className="font-mono text-sm font-semibold text-slate-900 py-2" data-testid="costing-tax-amount">{money(taxAmount)}</span>
                </div>
              </td>
            </tr>
            <tr>
              <td className="border border-slate-300 px-3 py-2 text-sm text-slate-600 bg-slate-50">Less Advance</td>
              <td className="border border-slate-300 p-0">
                <div className="flex items-center justify-end gap-2 px-2 py-1.5">
                  {canEditRole && (
                    <button type="button" onClick={openAdvanceDialog} title="Record an advance payment" data-testid="costing-advance-add-btn"
                      className="text-emerald-700 hover:bg-emerald-50 rounded-sm p-0.5"><Plus size={14} /></button>
                  )}
                  <span className="font-mono text-sm text-right text-slate-900" data-testid="costing-advance">{money(advanceSum)}</span>
                </div>
              </td>
            </tr>
            <tr>
              <td className="border border-slate-300 px-3 py-2.5 text-sm font-semibold text-slate-800 bg-slate-100">Total Amount</td>
              <td className="border border-slate-300 px-3 py-2.5 text-base font-mono font-bold text-right text-emerald-700 bg-slate-100" data-testid="costing-total-amount">{money(totalAmount)}</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Special note — placed after Total Amount */}
      <div className="mb-6">
        <label className="text-xs font-medium text-slate-600 mb-1.5 block">Special Note (visible on this Account Section)</label>
        <Textarea value={specialNote} onChange={(e) => setSpecialNote(e.target.value)} disabled={!canEdit} rows={2}
          className="border-slate-300 text-sm bg-white disabled:bg-slate-50 disabled:text-slate-500" data-testid="costing-special-note"
          placeholder="Any special instruction, discount note, or remark for this order's costing…" />
      </div>

      {canEdit ? (
        <div className="flex justify-end gap-2">
          {costing.line_items && canExport && (
            <>
              <Button onClick={downloadExcel} disabled={exporting} variant="outline" data-testid="export-excel-btn" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 rounded-sm gap-1.5">
                <FileXls size={15} /> {exporting ? "Preparing…" : "Download Excel"}
              </Button>
              <Button onClick={printQuotation} disabled={printing} variant="outline" data-testid="print-quotation-btn" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 rounded-sm gap-1.5">
                <Printer size={15} /> {printing ? "Preparing…" : "Print Quotation"}
              </Button>
            </>
          )}
          {canExport && (
            <Button onClick={printDeliveryNote} disabled={printingDelivery} variant="outline" data-testid="print-delivery-note-btn" className="border-sky-600 text-sky-700 hover:bg-sky-50 rounded-sm gap-1.5">
              <Printer size={15} /> {printingDelivery ? "Preparing…" : "Delivery Note"}
            </Button>
          )}
          <Button onClick={save} disabled={saving} data-testid="costing-save" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm">{saving ? "Saving…" : "Save Costing"}</Button>
        </div>
      ) : (
        <div className="flex justify-end items-center gap-2 flex-wrap">
          {costing.line_items && canExport && (
            <>
              <Button onClick={downloadExcel} disabled={exporting} variant="outline" data-testid="export-excel-btn" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 rounded-sm gap-1.5">
                <FileXls size={15} /> {exporting ? "Preparing…" : "Download Excel"}
              </Button>
              <Button onClick={printQuotation} disabled={printing} variant="outline" data-testid="print-quotation-btn" className="border-emerald-600 text-emerald-700 hover:bg-emerald-50 rounded-sm gap-1.5">
                <Printer size={15} /> {printing ? "Preparing…" : "Print Quotation"}
              </Button>
            </>
          )}
          {canExport && (
            <Button onClick={printDeliveryNote} disabled={printingDelivery} variant="outline" data-testid="print-delivery-note-btn" className="border-sky-600 text-sky-700 hover:bg-sky-50 rounded-sm gap-1.5">
              <Printer size={15} /> {printingDelivery ? "Preparing…" : "Delivery Note"}
            </Button>
          )}
          <p className="text-xs text-slate-500 flex items-center gap-1.5" data-testid="costing-readonly-note">
            <WarningCircle size={13} /> {locked ? "This sheet is locked. Contact your admin to request changes." : "Only an admin or this order's creator can edit costing — you're viewing in read-only mode."}
          </p>
        </div>
      )}

      {/* Advance payment history — this is a ledger of money actually received, so it's tracked
          and editable independently of the quotation being locked (see openAdvanceDialog/addAdvance) */}
      {advanceEntries.length > 0 && (
        <div className="mt-4 border-t border-slate-200 pt-4" data-testid="costing-advance-history">
          <div className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1.5"><ClockCounterClockwise size={13} /> Advance Payment History — running total {money(advanceSum)}</div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {[...advanceEntries].reverse().map((e) => (
              <div key={e.id} className="text-xs text-slate-500 flex items-center gap-2" data-testid={`costing-advance-entry-${e.id}`}>
                <span className="font-mono font-semibold text-emerald-700">{money(e.amount)}</span>
                <span>on {e.date}</span>
                {e.note && <span className="text-slate-400">· {e.note}</span>}
                <span className="text-slate-400">· recorded by {e.by_name}</span>
                {(user.role === "admin" || override) && (
                  <button type="button" onClick={() => deleteAdvance(e.id)} title="Remove this entry" data-testid={`costing-advance-delete-${e.id}`} className="text-slate-400 hover:text-red-500"><Trash size={12} /></button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Audit trail */}
      {(costing.history || []).length > 0 && (
        <div className="mt-6 border-t border-slate-200 pt-4" data-testid="costing-audit-trail">
          <div className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1.5"><ClockCounterClockwise size={13} /> Audit Trail — who edited, locked or unlocked this sheet</div>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {[...costing.history].reverse().map((h, i) => (
              <div key={i} className="text-xs text-slate-500 flex items-center gap-2" data-testid={`costing-history-${i}`}>
                <span className={`font-semibold ${h.action === "unlocked" ? "text-amber-600" : "text-emerald-700"}`}>{historyLabel(h)}</span>
                <span>by {h.by_name}</span>
                <span className="text-slate-400">· {new Date(h.at).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Record Advance Payment dialog */}
      <Dialog open={advanceDialogOpen} onOpenChange={setAdvanceDialogOpen}>
        <DialogContent data-testid="costing-advance-dialog" className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Record Advance Payment</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">Logs a payment received against this order. Doesn't require unlocking the sheet.</p>
          <div className="space-y-3 py-1">
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Amount received</label>
              <Input type="number" min="0" value={advanceAmount} onChange={(e) => setAdvanceAmount(e.target.value)} placeholder="e.g. 10000" data-testid="costing-advance-amount-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Date</label>
              <Input type="date" value={advanceDate} onChange={(e) => setAdvanceDate(e.target.value)} data-testid="costing-advance-date-input" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 block mb-1">Note (optional)</label>
              <Input value={advanceNote} onChange={(e) => setAdvanceNote(e.target.value)} placeholder="e.g. NEFT ref / cheque no." data-testid="costing-advance-note-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdvanceDialogOpen(false)} data-testid="costing-advance-cancel">Cancel</Button>
            <Button onClick={addAdvance} disabled={savingAdvance} data-testid="costing-advance-save" className="bg-emerald-600 hover:bg-emerald-700 text-white">{savingAdvance ? "Saving…" : "Record Advance"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manage Access dialog */}
      <Dialog open={!!manageFor} onOpenChange={(v) => !v && setManageFor(null)}>
        <DialogContent data-testid="manage-access-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle>Manage Access — {manageFor?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500 -mt-2">Only admin and the team members you check below can view this file. Everyone else sees it as Restricted.</p>
          <div className="max-h-64 overflow-y-auto space-y-2 py-2">
            {teamMembers.length === 0 && <div className="text-xs text-slate-400">No other team members found.</div>}
            {teamMembers.map((m) => (
              <label key={m.id} className="flex items-center gap-2.5 text-sm cursor-pointer" data-testid={`access-member-${m.id}`}>
                <Checkbox checked={selectedViewers.includes(m.id)} onCheckedChange={() => toggleViewer(m.id)} />
                <span className="text-slate-800">{m.name}</span>
                <span className="text-slate-400 text-xs capitalize">({m.role})</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setManageFor(null)} data-testid="manage-access-cancel">Cancel</Button>
            <Button onClick={saveAccess} disabled={savingAccess} data-testid="manage-access-save" className="bg-emerald-600 hover:bg-emerald-700 text-white">{savingAccess ? "Saving…" : "Save Access"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
